import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { THEMES } from "../../types/ThemeTypes";
import "./PlaylistsFeed.css";

interface Playlist {
    _id: number;
    name: string;
    description: string;
    is_public: boolean;
    theme: string;
    created_at: string;
    updated_at: string;
    user_id: number;
    picture: string;
    dubu_user: string;
}

interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasMore: boolean;
}

const PlaylistsFeedPage: React.FC = () => {
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationInfo>({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasMore: true
    });

    const observer = useRef<IntersectionObserver | null>(null);
    const loadingRef = useCallback((node: HTMLDivElement | null) => {
        if (isLoading) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && pagination.hasMore) {
                loadMorePlaylists();
            }
        });

        if (node) observer.current.observe(node);
    }, [isLoading, pagination.hasMore]);

    const fetchPlaylists = async (page: number) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/playlists?page=${page}&limit=12`);

            if (!response.ok) {
                throw new Error(`Failed to fetch playlists: ${response.status}`);
            }

            const data = await response.json();

            if (page === 1) {
                setPlaylists(data.playlists);
            } else {
                setPlaylists(prev => [...prev, ...data.playlists]);
            }

            setPagination(data.pagination);
        } catch (err) {
            console.error("Error fetching playlists:", err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsLoading(false);
        }
    };

    const loadMorePlaylists = () => {
        if (isLoading || !pagination.hasMore) return;
        fetchPlaylists(pagination.currentPage + 1);
    };

    useEffect(() => {
        fetchPlaylists(1);
    }, []);

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const renderSkeletonCards = () => {
        return Array(8).fill(0).map((_, index) => (
            <div key={`skeleton-${index}`} className="playlist-feed-card">
                <div className="playlist-feed-skeleton-card">
                    <div className="playlist-feed-skeleton playlist-feed-skeleton-theme"></div>
                    <div className="playlist-feed-skeleton-content">
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-title"></div>
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-description"></div>
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-description"></div>
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-description"></div>
                    </div>
                    <div className="playlist-feed-skeleton-footer">
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-user"></div>
                        <div className="playlist-feed-skeleton playlist-feed-skeleton-date"></div>
                    </div>
                </div>
            </div>
        ));
    };

    return (
        <div className="playlists-feed-page">
            <div className="playlists-feed-container">
                <div className="playlists-feed-header">
                    <h1>Public Playlists</h1>
                    <p>Discover playlists created by the community</p>
                </div>

                {error && (
                    <div className="playlists-feed-error">
                        <p>Error: {error}</p>
                        <button onClick={() => fetchPlaylists(1)}>Retry</button>
                    </div>
                )}

                <div className="playlists-feed-grid">
                    {playlists.map(playlist => (
                        <Link
                            to={`/playlist/${playlist._id}`}
                            key={playlist._id}
                            className="playlist-feed-card"
                            style={{
                                backgroundColor: playlist.theme && THEMES[playlist.theme as keyof typeof THEMES]
                                    ? THEMES[playlist.theme as keyof typeof THEMES].color
                                    : THEMES.SING.color
                            }}
                        >
                            <div className="playlist-feed-card-footer">
                                <div className="playlist-feed-card-user">
                                    <img src={playlist.picture || "/default-profile.png"} alt="User"
                                        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                                            event.currentTarget.src = "/dubuFace260.jpg";
                                            event.currentTarget.onerror = null;
                                        }}
                                    />
                                    <span>{playlist.dubu_user}</span>
                                </div>
                                <div className="playlist-feed-card-date">
                                    {formatDate(playlist.created_at)}
                                </div>
                            </div>

                            <div className="playlist-feed-card-content">
                                <div className="playlist-feed-card-body">
                                    <h3 className="playlist-feed-card-title">{playlist.name}</h3>
                                    <p className="playlist-feed-card-description">
                                        {playlist.description || "No description provided"}
                                    </p>
                                </div>
                                <div className="playlist-feed-card-theme">
                                    <img
                                        src={playlist.theme && THEMES[playlist.theme as keyof typeof THEMES]
                                            ? THEMES[playlist.theme as keyof typeof THEMES].image
                                            : THEMES.SING.image}
                                        alt="Playlist theme"
                                    />
                                </div>
                            </div>

                        </Link>
                    ))}

                    {isLoading && playlists.length === 0 && renderSkeletonCards()}
                </div>

                {isLoading && playlists.length > 0 && (
                    <div className="playlists-feed-loading">
                        <div className="playlists-feed-loading-spinner"></div>
                    </div>
                )}

                {!isLoading && pagination.hasMore && (
                    <div ref={loadingRef} style={{ height: "20px" }}></div>
                )}

                {!isLoading && !pagination.hasMore && playlists.length > 0 && (
                    <div className="playlist-feed-no-more">
                        No more playlists to load
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlaylistsFeedPage; 